import { API_BASE_URL } from '@/config/api-url';
import { APP_GUID_KEY, hasSdkAppGuid, resolveAppGuid } from '@/utils/appGuid';
import {
  getRevokedVisitorId,
  peekVisitorId,
  __resetVisitorIdCacheForTests,
} from './visitorId';
import { getSessionId, __resetSessionIdCacheForTests } from './sessionId';
import {
  captureUtmFromUrl,
  getStoredUtm,
  getFirstTouchUtmClickIdFields,
  resetUtmCache,
} from './utmCapture';
import { captureClickIds, getStoredClickIds } from './clickIdCapture';
import { resolvePlayerConsent } from './playerPixelEvents';
import { resolveSharingConsent } from './euConsent';
import {
  enqueueAnalyticsRequest,
  __resetAnalyticsQueueForTests,
} from './eventQueue';

export type TrackTarget =
  | { kind: 'quiz'; projectId: string; slug: string }
  | { kind: 'paywall'; projectId: string; slug: string };

function endpoint(target: TrackTarget): string {
  const { kind, projectId, slug } = target;
  return `${API_BASE_URL}/public/${kind}/by-id/${projectId}/${slug}/event`;
}

/**
 * Известна ли личность ПРИЛОЖЕНИЯ (`?origin=app&guid=` в адресе или уже
 * сохранённый app-guid). Только ЧТЕНИЕ: правило приоритета остаётся одно, в
 * `utils/appGuid.ts` — здесь лишь ответ на вопрос «есть ли что резолвить, не
 * заводя нового идентификатора».
 */
function hasKnownAppIdentity(): boolean {
  try {
    if (sessionStorage.getItem(APP_GUID_KEY)) return true;
  } catch {
    /* приватный режим — считаем, что личности приложения нет */
  }
  try {
    return hasSdkAppGuid(new URLSearchParams(window.location.search));
  } catch {
    return false;
  }
}

/**
 * Ревизия пачки 45 (находка C1) — идентификатор при ЯВНОМ ОТКАЗЕ.
 *
 * Это не сбор, а ОТЗЫВ: строка согласия в базе пишется только по ключу
 * посетителя (`recordIdentity`), и отказ без ключа не отменял ничего — в базе
 * оставалось прежнее «granted», а значит уходили и постбэк партнёру, и
 * серверная конверсия, и Purchase-пиксель.
 *
 * Границы отзыва:
 *  - НОВЫЙ идентификатор не заводится никогда (`getVisitorId()` создал бы его
 *    и записал на устройство — это был бы ровно тот сбор, от которого человек
 *    отказался). Не был известен — отзывать нечего, поля нет;
 *  - если след уже стёрт кнопкой «Отклонить», ключ берётся из памяти страницы
 *    (`getRevokedVisitorId`), а не с устройства.
 *
 * ⚠ Граница честно: память страницы живёт до перезагрузки. Отказ, после
 * которого посетитель сразу закрыл вкладку и не совершил больше ни одного
 * события, до сервера не доедет — для этого нужен отдельный тип события
 * «отзыв», а он меняет приёмный контракт (вопрос владельцу, WEB-2259).
 * Закрыто позже отдельной дверью: решение с баннера уходит сразу
 * (`reportConsentDecision`, WEB-2604), а недоставленный отказ переживает
 * закрытие вкладки отметкой на устройстве (`consentReport.ts`, WEB-2672).
 */
function resolveRevocationVisitorId(): string | undefined {
  if (peekVisitorId() !== null || hasKnownAppIdentity()) {
    // Тот же резолв личности, что у согласившегося: сервер знает посетителя
    // именно под ним, и отзывать надо по нему же.
    return resolveAppGuid();
  }
  return getRevokedVisitorId() ?? undefined;
}

export function trackEvent(
  target: TrackTarget,
  event: string,
  payload: Record<string, unknown> = {},
): void {
  // WEB-2145 п.1 / WEB-2146 — РЕШЕНИЕ ПОСЕТИТЕЛЯ ГАСИТ САМ СБОР, а не только
  // отдачу наружу.
  //
  // Раньше захват меток (то есть ЗАПИСЬ кук на устройство) и чтение уже
  // сохранённых данных шли безусловно, а решение читалось только чтобы
  // положить его в тело события. Отсюда два дефекта сразу: у европейца метки
  // появлялись ДО ответа на баннер (WEB-2146), а после «Отклонить»
  // продолжали считываться в каждое событие (WEB-2145 п.1).
  //
  // Теперь весь атрибуционный блок стоит под `granted`. Вне списка стран
  // ничего не меняется: там согласие проставляется автоматически ДО первого
  // события (PublicQuiz/PublicPaywall, ветка `!requiresEuConsent`), поэтому к
  // этому месту кука уже 'granted'.
  //
  // ⚠ Границы, прямо названные в WEB-2145 §4 п.1: идентификатор ПОСЕЩЕНИЯ
  // (`sessionId`) и само событие не трогаются — на них держатся счётчики
  // воронки, и отказавшийся посетитель обязан оставаться виден в статистике.
  const decision = resolvePlayerConsent();
  const mayCollectAttribution = decision === 'granted';

  if (mayCollectAttribution) {
    // Capture UTM (WEB-1436: куки, last-touch + first-touch — зеркало кликов)
    // + ad click-ids (last-touch _fbc) from the URL.
    captureUtmFromUrl();
    captureClickIds();
  }

  // WEB-1436: первое касание UTM едет внутри clickIds (ftUtmSource…) — тем же
  // приёмом, что ftParam/ftValue: jsonb click_ids без миграции; денежный путь
  // (stripe-webhook.processor, CLICK_ID_FIELDS/pickClickIds) поднимает его в
  // событие покупки. Поля объявлены в ClickIdsDto этим же атомом — иначе
  // forbidNonWhitelisted отбил бы ВСЁ событие (шрам WEB-933 п.3).
  const clickIds = mayCollectAttribution
    ? {
        ...getStoredClickIds(),
        ...getFirstTouchUtmClickIdFields(),
      }
    : {};
  // Ревизия 5 (дефект 1): решение посетителя о согласии (баннер → кука
  // w2a_consent) едет в теле события. Бэкенд принимает необязательное
  // `consent` (granted|denied) и остаётся арбитром: вне ЕС значение
  // игнорируется, в ЕС без него согласие не записывалось НИКОГДА — серверные
  // конверсии по таким посетителям не уходили.
  // ⚠ Поле обязано быть в приёмном формате бэкенда: при forbidNonWhitelisted
  // незнакомое поле отбивает ВСЁ событие 400-й (шрам clickIds, WEB-933 п.3).
  //
  // WEB-2162 п.4: сигнал браузера Global Privacy Control перекрывает решение,
  // данное на баннере, — но только в сторону запрета ПЕРЕДАЧИ третьим лицам.
  // Поле `consent` и есть тот рычаг, по которому сервер решает, отправлять ли
  // серверную конверсию и постбэк; собственная аналитика ниже не меняется.
  const consent = resolveSharingConsent(decision);
  // Ревизия 45 (C1): при явном отказе ключ отзыва — см. resolveRevocationVisitorId.
  const revocationVisitorId =
    decision === 'denied' ? resolveRevocationVisitorId() : undefined;
  // WEB-1182: квиз, открытый из приложения (?origin=app&guid=X), уже платит
  // под X (utils/appGuid.ts, money-review-guarded приоритет). Аналитика
  // раньше слала веб-visitorId безусловно — сервер материализует свойства
  // пользователя по ключу события, и запрос свойств по X возвращал пусто.
  // resolveAppGuid() — ТОТ ЖЕ резолв и ТА ЖЕ утилита, что у оплаты (не
  // дублировать приоритет здесь): app-guid из адреса/sessionStorage, иначе
  // обычный веб visitor-id — обычная воронка без параметров не меняет
  // поведение ни на йоту.
  const body = {
    event,
    // WEB-2145 п.1: постоянный идентификатор посетителя — тоже атрибуционные
    // данные. Поле необязательное в приёмном формате сервера, поэтому без
    // согласия его просто нет (шрам 2026-08-24: путь без идентификатора
    // сервером предусмотрен).
    // Ревизия 45 (C1): у отказавшегося поле есть, но означает другое — это
    // КЛЮЧ ОТЗЫВА, по которому сервер снимает ранее данное согласие.
    ...(mayCollectAttribution
      ? { visitorId: resolveAppGuid() }
      : revocationVisitorId
        ? { visitorId: revocationVisitorId }
        : {}),
    sessionId: getSessionId(),
    ...(mayCollectAttribution ? getStoredUtm() : {}),
    // P8 §S8 — forward ad click-ids (fbc/fbp) only when present.
    ...(Object.keys(clickIds).length > 0 ? { clickIds } : {}),
    // Решения нет → поля нет (оно необязательное).
    ...(consent ? { consent } : {}),
    // payload главнее: явный consent на месте вызова перекрывает куку.
    ...payload,
  };

  // WEB-1993 п.2: тело СОБРАНО здесь (значения обязаны быть теми, что были в
  // момент события: адрес, куки, sessionStorage), а вот сериализация и сеть
  // уезжают с пути клика — их делает очередь после отрисовки. Возврат из
  // `trackEvent` синхронный и сети не касается.
  enqueueAnalyticsRequest(endpoint(target), () => JSON.stringify(body));
}

/**
 * WEB-2604 (решение владельца 29.09) — решение с баннера уходит на сервер
 * СРАЗУ, отдельным сообщением, а не попутно со следующим событием: иначе
 * «Отклонить» и закрытая вкладка оставляли сервер при старом согласии, и
 * покупка уходила в Meta/TikTok.
 *
 * Зовут только кнопки баннера (не авто-согласие) — ПОСЛЕ
 * `applyConsentDecision`, когда ключ отзыва уже переложен очисткой. Тело —
 * три поля: ключ (при отказе — ключ отзыва, новый не заводится), решение с
 * учётом GPC, момент нажатия. Меток клика и кук сетей здесь нет. Доставку до
 * подтверждения ведёт лениво загружаемый `consentReport.ts` (вес первого
 * экрана): его модуль ничего не импортирует, поэтому не дробит чанки.
 */
export function reportConsentDecision(
  projectId: string,
  decision: 'granted' | 'denied',
): void {
  const guid =
    decision === 'granted' ? resolveAppGuid() : resolveRevocationVisitorId();
  if (!guid) return; // ключ не известен — на сервере нечего отзывать
  const body = JSON.stringify({
    guid,
    consent: resolveSharingConsent(decision) ?? decision,
    decidedAt: Date.now(),
  });
  // WEB-2672: адрес двери собирает модуль доставки — ему ключ проекта нужен
  // ещё и для отметки недоставленного отказа на устройстве.
  import('./consentReport')
    .then((m) => m.sendConsentReport(API_BASE_URL, projectId, body))
    .catch(() => undefined);
}

/**
 * WEB-2672 — дослать отказ, оставшийся недоставленным с прошлой загрузки
 * (отметка на устройстве, см. `consentReport.ts`). Зовёт вход публичной
 * воронки (`public-main.tsx`) в простое браузера. Живёт здесь, рядом с
 * таким же ленивым импортом в `reportConsentDecision`, а не во входе: так
 * сжатие первого экрана делит одинаковый код, и вес растёт меньше.
 */
export function resumeConsentReports(): void {
  import('./consentReport')
    .then((m) => m.resumeConsentReports(API_BASE_URL))
    .catch(() => undefined);
}

export function __resetTrackEventForTests(): void {
  __resetAnalyticsQueueForTests();
  __resetVisitorIdCacheForTests();
  __resetSessionIdCacheForTests();
  resetUtmCache();
}
